import { isServer } from '@mr-yum/frontend-core/dist/support/env'
import { GiftFilledIcon, StarFilledIcon } from '@mr-yum/frontend-ui'
import { IconCard } from 'components/Common/IconCard'
import { AuraMemberShipCookie } from 'components/Rewards/hooks/useAuraOAuth'
import { useLogger } from 'hooks/useLogger'
import Cookies from 'js-cookie'
import { useLoyaltyDetailsQuery, UserPartsFragment } from 'lib/gql'
import { formatPoints } from 'lib/utils'
import React, { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { LoyaltyFormModal } from './LoyaltyFormModal'

interface Props {
  venueSlug: string
  user: UserPartsFragment
  tabId?: boolean
  orderId?: string
}

export const Loyalty = ({ venueSlug, user, tabId, orderId }: Props) => {
  const intl = useIntl()
  const { logEvent } = useLogger()

  const [{ data, fetching }] = useLoyaltyDetailsQuery({
    variables: {
      venueSlug,
      externalMemberId:
        Cookies.get(AuraMemberShipCookie + '-' + venueSlug) || null,
    },
    requestPolicy: 'network-only',
    pause: isServer,
  })

  const [newlySignedUp, setNewlySignedUp] = useState(false)
  const [loyaltyModalOpen, setLoyaltyModalOpen] = useState(false)

  const loyaltyDetails = data?.loyaltyDetails
  const metadata = loyaltyDetails?.metadata

  const pointsData = {
    pointName:
      metadata?.pointName ||
      intl.formatMessage({ defaultMessage: 'points', id: 'KYUOY2' }),
    programName:
      metadata?.programName ||
      intl.formatMessage({ defaultMessage: 'loyalty', id: 'yU6q1x' }),
    points: formatPoints(loyaltyDetails?.lastOrder?.points),
  }

  const onIconCardClick = useCallback(() => {
    logEvent(
      'Clicked on terms and conditions on order confirmation (loyalty)',
      {
        orderId,
        tabId,
      },
    )
  }, [logEvent, orderId, tabId])

  if (fetching || !loyaltyDetails || !loyaltyDetails.features.canEarnPoints) {
    return null
  }

  if (newlySignedUp) {
    return (
      <div className="space-y-5 px-4 py-5">
        <h3 className="my-heading-sm">
          <FormattedMessage
            defaultMessage="Your {programName}"
            id="ufaiuP"
            values={pointsData}
          />
        </h3>
        <IconCard
          as="div"
          showIndicator={false}
          heading={intl.formatMessage({
            defaultMessage: 'You’re all signed up!',
            id: 'agbkpw',
          })}
          description={
            <>
              <FormattedMessage
                defaultMessage="You’ve earned {points} {programName} {pointName}. Your {pointName} can be redeemed at checkout."
                id="ZIbHI1"
                values={pointsData}
              />
              <br />
              <br />
              <a
                className="inline-block text-foreground-subtle underline"
                rel="noreferrer noopener"
                href="https://www.mryum.com/tou?utm_source=mryum"
                target="_blank"
                onClick={onIconCardClick}
              >
                <FormattedMessage
                  defaultMessage="Terms & conditions"
                  id="bPyOFk"
                />
              </a>
            </>
          }
          icon={<StarFilledIcon />}
        />
      </div>
    )
  }

  if (
    !loyaltyDetails?.lastOrder?.points ||
    loyaltyDetails?.lastOrder?.points <= 0
  ) {
    return null
  }

  if (loyaltyDetails?.guestData?.isOptedIn) {
    return (
      <div className="space-y-5 px-4 py-5">
        <h3 className="my-heading-sm">
          <FormattedMessage
            defaultMessage="Your {programName}"
            id="ufaiuP"
            values={pointsData}
          />
        </h3>
        <IconCard
          as="div"
          showIndicator={false}
          heading={intl.formatMessage(
            {
              defaultMessage:
                'You’ve earned {points} {programName} {pointName} with this purchase.',
              id: 'rbLjjE',
            },
            pointsData,
          )}
          description={intl.formatMessage(
            {
              defaultMessage: 'Your {pointName} can be redeemed at checkout.',
              id: 'TGV86f',
            },
            pointsData,
          )}
          icon={<StarFilledIcon />}
        />
      </div>
    )
  }

  return (
    <div className="space-y-5 px-4 py-5">
      <h3 className="my-heading-sm">
        <FormattedMessage
          defaultMessage="Join {programName}"
          id="iGqW4Q"
          values={pointsData}
        />
      </h3>
      <IconCard
        as="button"
        onClick={() => setLoyaltyModalOpen(true)}
        data-testid="loyalty-card"
        heading={intl.formatMessage(
          {
            defaultMessage: 'Join our free {programName} program!',
            id: 'PTTEZb',
          },
          pointsData,
        )}
        description={intl.formatMessage(
          {
            defaultMessage:
              'You could earn {points} {programName} {pointName} to redeem next time you visit.',
            id: 'IO1ouv',
          },
          pointsData,
        )}
        icon={<GiftFilledIcon />}
      />

      <LoyaltyFormModal
        email={user?.email}
        isOpen={loyaltyModalOpen}
        minPoints={metadata?.minimumPointsRequired}
        pointsData={pointsData}
        onClose={() => setLoyaltyModalOpen(false)}
        onSuccess={() => setNewlySignedUp(true)}
      />
    </div>
  )
}
